/**
 * CityLanding — SEO-rich landing page per ogni città italiana con strutture pubblicate.
 * Route: /case-vacanze/:slug
 *
 * SEO stack:
 *  - dynamic <title>, <meta description>, OG, Twitter card, og:image, og:locale, robots
 *  - canonical + hreflang it-IT
 *  - h1/h2/h3 strutturati con keyword long-tail (regione, provincia, CAP)
 *  - Visible breadcrumb (Home › Regione › Provincia › Città)
 *  - JSON-LD: ItemList, BreadcrumbList, Place, FAQPage
 *  - "Città vicine" linkate per migliorare il pagerank interno
 *  - FAQ contestuali (5 domande) renderizzate visibili E in JSON-LD
 *  - Stats territoriali: CAP, popolazione, provincia, regione
 */
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin, Users, Bed, Bath, ArrowRight, Sparkles, ShieldCheck, Euro, BadgeCheck,
  ChevronRight, Building2, Star, HelpCircle
} from "lucide-react";
import { API } from "../App";

const ACCENT = "#FF385C";
const PROD_DOMAIN = "https://hostland.vitadahost.it";

const titleCase = (s = "") => s.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substr(1).toLowerCase());

const updateMeta = (name, content, attr = "name") => {
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const setLink = (id, rel, href, extra = {}) => {
  let tag = document.getElementById(id);
  if (!tag) {
    tag = document.createElement("link");
    tag.id = id;
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
  Object.entries(extra).forEach(([k, v]) => tag.setAttribute(k, v));
};

const setJsonLd = (id, data) => {
  let tag = document.getElementById(id);
  if (!tag) {
    tag = document.createElement("script");
    tag.setAttribute("type", "application/ld+json");
    tag.id = id;
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
};

const CityLanding = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({
    comune: null, properties: [], total: 0,
    region: null, province: null, province_code: null, postal_codes: [],
    population: null, centroid: null, capacity_stats: null,
    amenities_top: [], nearby_cities: []
  });
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const r = await fetch(`${API}/cities/${slug}/properties`);
        if (r.status === 404) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        const j = await r.json();
        setData(j);
      } catch {
        setData({ comune: null, properties: [], total: 0, region: null, province: null });
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // SEO — city gone (unpublished or never existed): mark noindex so Google
  // drops the URL at the next crawl. Without this, Search Console flags the
  // page as "Scansionata ma non indicizzata" indefinitely.
  useEffect(() => {
    if (!notFound) return;
    document.title = "Città non trovata · Hostland";
    let robots = document.querySelector("meta[name='robots']");
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, follow");
    // Cleanup: restore the default when the user navigates away.
    return () => {
      robots.setAttribute("content", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    };
  }, [notFound]);

  const city = useMemo(() => titleCase(data.comune || ""), [data.comune]);

  // ─── FAQ contestuali — visibili E iniettate in JSON-LD FAQPage schema ───
  const faqs = useMemo(() => {
    if (!data.comune) return [];
    const c = city;
    const count = data.total || 0;
    const region = data.region || "Italia";
    return [
      {
        q: `Quante case vacanze ci sono a ${c} su Hostland?`,
        a: `Attualmente sono pubblicate ${count} struttur${count === 1 ? "a verificata" : "e verificate"} a ${c}: case vacanze, B&B, affitti brevi e boutique hotel. Tutte mostrano il CIN obbligatorio e permettono la prenotazione diretta con l'host senza commissioni di intermediazione.`,
      },
      {
        q: `Si può prenotare direttamente con l'host a ${c} senza commissioni?`,
        a: `Sì. Ogni struttura su Hostland mostra il contatto diretto dell'host (telefono, email, WhatsApp). Prenotando direttamente risparmi il 15-20% rispetto alle commissioni applicate da Airbnb o Booking.com e spesso ottieni sconti riservati dal concierge locale.`,
      },
      {
        q: `Quali zone di ${c} sono coperte da Hostland?`,
        a: `Hostland copre tutta la città di ${c}${data.province ? ` (provincia di ${data.province}, ${region})` : ""}${data.postal_codes && data.postal_codes.length ? `, dai CAP ${data.postal_codes[0]} a ${data.postal_codes[data.postal_codes.length - 1]}` : ""}. Le strutture sono distribuite fra centro storico, quartieri residenziali e zone turistiche.`,
      },
      {
        q: `Le strutture a ${c} sono in regola con la normativa italiana?`,
        a: `Sì. Tutte le strutture su Hostland sono obbligate a pubblicare il CIN (Codice Identificativo Nazionale) emesso dal Ministero del Turismo. Molte mostrano anche il CIR regionale dove richiesto. Hostland non pubblica strutture senza CIN, garantendo conformità legale al 100%.`,
      },
      {
        q: `Posso lasciare una recensione dopo il soggiorno a ${c}?`,
        a: `Sì. Ogni struttura include un GuestBook digitale dove gli ospiti possono lasciare feedback e suggerimenti. Inoltre le recensioni Google Business vengono importate e aggiornate automaticamente ogni 7 giorni per garantire la trasparenza.`,
      },
    ];
  }, [city, data.comune, data.total, data.region, data.province, data.postal_codes]);

  // ─── SEO meta tags + JSON-LD injection ───
  useEffect(() => {
    if (!data.comune) return;
    const c = city;
    const count = data.total || 0;
    const region = data.region || "";
    const province = data.province || "";
    const provCode = data.province_code || "";

    const title = region
      ? `Case vacanze a ${c} (${provCode || province}) senza commissioni — ${count} struttur${count === 1 ? "a" : "e"} | Hostland`
      : `Case vacanze a ${c} senza commissioni — ${count} struttur${count === 1 ? "a" : "e"} | Hostland`;
    const description = `Trova case vacanze, B&B e affitti brevi a ${c}${province ? `, provincia di ${province}` : ""}${region ? ` in ${region}` : ""} su Hostland. Prenota direttamente con l'host, ZERO commissioni di intermediazione. ${count} struttur${count === 1 ? "a verificata" : "e verificate"} con CIN obbligatorio.`;
    const canonical = `${PROD_DOMAIN}/case-vacanze/${slug}`;
    const firstImage = data.properties.find((p) => p.media?.[0])?.media?.[0];
    const ogImage = firstImage ? `${PROD_DOMAIN}/api/files/${firstImage}` : `${PROD_DOMAIN}/hostland-social.png`;

    document.title = title;
    updateMeta("description", description);
    updateMeta(
      "keywords",
      `case vacanze ${c.toLowerCase()}, affitti brevi ${c.toLowerCase()}, B&B ${c.toLowerCase()}, alloggi senza commissioni ${c.toLowerCase()}, prenotazione diretta ${c.toLowerCase()}, dove dormire ${c.toLowerCase()}, host ${c.toLowerCase()}, ${province ? `provincia ${province.toLowerCase()}, ` : ""}${region ? `${region.toLowerCase()}, ` : ""}hostland`
    );
    updateMeta("robots", "index, follow, max-image-preview:large");
    updateMeta("og:title", title, "property");
    updateMeta("og:description", description, "property");
    updateMeta("og:url", canonical, "property");
    updateMeta("og:type", "website", "property");
    updateMeta("og:image", ogImage, "property");
    updateMeta("og:locale", "it_IT", "property");
    updateMeta("og:site_name", "Hostland", "property");
    updateMeta("twitter:card", "summary_large_image");
    updateMeta("twitter:title", title);
    updateMeta("twitter:description", description);
    updateMeta("twitter:image", ogImage);

    setLink("city-canonical", "canonical", canonical);
    setLink("city-hreflang-it", "alternate", canonical, { hreflang: "it-IT" });
    setLink("city-hreflang-default", "alternate", canonical, { hreflang: "x-default" });

    // ─── JSON-LD ItemList for properties in this city ───
    const ld = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: title,
      description,
      url: canonical,
      numberOfItems: count,
      itemListElement: data.properties.slice(0, 20).map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "LodgingBusiness",
          name: p.name,
          url: p.custom_slug ? `${PROD_DOMAIN}/s/${p.custom_slug}` : `${PROD_DOMAIN}/property/${p.property_id}`,
          ...(p.guests_count ? { numberOfRooms: p.rooms_count, maximumAttendeeCapacity: p.guests_count } : {}),
          address: {
            "@type": "PostalAddress",
            addressLocality: c,
            ...(province ? { addressRegion: provCode || province } : {}),
            addressCountry: "IT",
          },
          ...(p.location_lat && p.location_lng
            ? { geo: { "@type": "GeoCoordinates", latitude: p.location_lat, longitude: p.location_lng } }
            : {}),
          ...(p.media?.[0] ? { image: `${PROD_DOMAIN}/api/files/${p.media[0]}` } : {}),
        },
      })),
    };
    setJsonLd("city-jsonld-itemlist", ld);

    // ─── JSON-LD BreadcrumbList ───
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Hostland", item: PROD_DOMAIN },
        ...(region ? [{ "@type": "ListItem", position: 2, name: region, item: `${PROD_DOMAIN}/case-vacanze?regione=${encodeURIComponent(region.toLowerCase())}` }] : []),
        ...(province ? [{ "@type": "ListItem", position: region ? 3 : 2, name: `Provincia di ${province}`, item: `${PROD_DOMAIN}/case-vacanze?provincia=${encodeURIComponent((provCode || province).toLowerCase())}` }] : []),
        { "@type": "ListItem", position: 2 + (region ? 1 : 0) + (province ? 1 : 0), name: c, item: canonical },
      ],
    };
    setJsonLd("city-jsonld-breadcrumb", breadcrumb);

    // ─── JSON-LD Place ───
    const place = {
      "@context": "https://schema.org",
      "@type": "City",
      name: c,
      url: canonical,
      ...(data.centroid
        ? { geo: { "@type": "GeoCoordinates", latitude: data.centroid.lat, longitude: data.centroid.lng } }
        : {}),
      ...(province ? { containedInPlace: { "@type": "AdministrativeArea", name: `Provincia di ${province}` } } : {}),
      ...(region ? { addressRegion: region } : {}),
      address: { "@type": "PostalAddress", addressLocality: c, addressCountry: "IT", ...(provCode ? { addressRegion: provCode } : {}) },
    };
    setJsonLd("city-jsonld-place", place);

    // ─── JSON-LD FAQPage ───
    if (faqs.length) {
      const faqLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      };
      setJsonLd("city-jsonld-faq", faqLd);
    }

    return () => {
      ["city-jsonld-itemlist", "city-jsonld-breadcrumb", "city-jsonld-place", "city-jsonld-faq"]
        .forEach((id) => document.getElementById(id)?.remove());
    };
  }, [data, slug, city, faqs]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50" data-prerender-pending="1">
        <div className="w-10 h-10 border-4 border-zinc-200 border-t-zinc-800 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data.comune || notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-50 p-6 text-center">
        <MapPin className="w-12 h-12 text-zinc-300 mb-3" />
        <h1 className="text-2xl font-bold text-zinc-900 mb-2">Città non trovata</h1>
        <p className="text-zinc-500 mb-6">Nessuna struttura pubblicata per questa città su Hostland.</p>
        <button onClick={() => navigate("/")} className="bg-zinc-900 text-white font-bold px-6 py-3 rounded-2xl" data-testid="city-back-not-found">Torna alla home</button>
      </div>
    );
  }

  const { region, province, province_code, postal_codes, population, nearby_cities, capacity_stats } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-white">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-12">
        {/* ─── Breadcrumb (visible) ─── */}
        <nav aria-label="breadcrumb" className="flex flex-wrap items-center gap-1.5 text-xs text-zinc-500 mb-6" data-testid="city-breadcrumb">
          <Link to="/" className="hover:text-zinc-900 font-medium">Hostland</Link>
          <ChevronRight className="w-3 h-3 text-zinc-300" />
          <span className="text-zinc-400">Case vacanze</span>
          {region && (
            <>
              <ChevronRight className="w-3 h-3 text-zinc-300" />
              <span className="text-zinc-500">{region}</span>
            </>
          )}
          {province && (
            <>
              <ChevronRight className="w-3 h-3 text-zinc-300" />
              <span className="text-zinc-500">{province}</span>
            </>
          )}
          <ChevronRight className="w-3 h-3 text-zinc-300" />
          <span className="text-zinc-900 font-bold">{city}</span>
        </nav>

        {/* ─── Hero ─── */}
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
            <Euro className="w-3.5 h-3.5 text-emerald-700" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Zero commissioni</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tighter text-zinc-900 leading-[1.05]" data-testid="city-h1">
            Case vacanze a <span style={{ color: ACCENT }}>{city}</span>
            <br className="hidden md:block" />
            <span className="text-zinc-400 font-bold"> senza commissioni</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-zinc-600 max-w-2xl leading-relaxed">
            Su <strong>Hostland</strong> trovi {data.total} struttur{data.total === 1 ? "a verificata" : "e verificate"} a <strong>{city}</strong>
            {province && <> in <strong>provincia di {province}</strong></>}
            {region && <>, regione <strong>{region}</strong></>}
            : case vacanze, B&amp;B, affitti brevi e boutique hotel. Prenota direttamente con l'host, <strong>risparmia il 15-20%</strong> rispetto alle OTA, ottieni sconti riservati e un'esperienza autentica.
          </p>

          {/* Territory facts row */}
          {(population || (postal_codes && postal_codes.length > 0) || province_code) && (
            <div className="flex flex-wrap gap-3 mt-5 text-xs text-zinc-500" data-testid="city-territory-facts">
              {province_code && (
                <span className="inline-flex items-center gap-1.5 bg-zinc-100 px-2.5 py-1 rounded-full">
                  <Building2 className="w-3 h-3" /> Sigla <strong className="text-zinc-700">{province_code}</strong>
                </span>
              )}
              {population && (
                <span className="inline-flex items-center gap-1.5 bg-zinc-100 px-2.5 py-1 rounded-full">
                  <Users className="w-3 h-3" /> <strong className="text-zinc-700">{population.toLocaleString("it-IT")}</strong> abitanti
                </span>
              )}
              {postal_codes && postal_codes.length > 0 && (
                <span className="inline-flex items-center gap-1.5 bg-zinc-100 px-2.5 py-1 rounded-full">
                  <MapPin className="w-3 h-3" /> CAP {postal_codes[0]}{postal_codes.length > 1 ? `–${postal_codes[postal_codes.length - 1]}` : ""}
                </span>
              )}
            </div>
          )}

          {/* Trust pills */}
          <div className="flex flex-wrap gap-2 mt-6">
            {[
              { Icon: Euro, label: "Zero commissioni", color: "emerald" },
              { Icon: BadgeCheck, label: "Host verificati", color: "blue" },
              { Icon: ShieldCheck, label: "CIN obbligatorio mostrato", color: "amber" },
              { Icon: Sparkles, label: "Sconti riservati", color: "violet" },
            ].map(({ Icon, label, color }) => (
              <div key={label} className={`inline-flex items-center gap-1.5 bg-${color}-50 border border-${color}-200 px-3 py-1.5 rounded-full`}>
                <Icon className={`w-3 h-3 text-${color}-700`} />
                <span className={`text-[11px] font-bold text-${color}-700`}>{label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ─── SEO content blocks ─── */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl p-6 border border-zinc-100">
            <h2 className="text-lg font-bold text-zinc-900 mb-3">Perché prenotare a {city} con Hostland?</h2>
            <ul className="space-y-2 text-sm text-zinc-600">
              <li>✓ <strong>Risparmi 15-20%</strong> evitando le commissioni Airbnb/Booking</li>
              <li>✓ Ottieni <strong>sconti riservati</strong> dal concierge locale dell'host</li>
              <li>✓ Tutte le strutture hanno il <strong>CIN obbligatorio</strong> pubblicato</li>
              <li>✓ Mappa dei <strong>POI</strong> consigliati personalmente dall'host</li>
              <li>✓ <strong>Multilingua</strong> (IT/EN/FR/ES/DE) e GuestBook digitale incluso</li>
            </ul>
          </div>
          <div className="bg-white rounded-3xl p-6 border border-zinc-100">
            <h2 className="text-lg font-bold text-zinc-900 mb-3">Cosa puoi trovare a {city}</h2>
            <p className="text-sm text-zinc-600 leading-relaxed">
              A <strong>{city}</strong>{province && <> (provincia di {province}{region && `, ${region}`})</>} sono disponibili {data.total} struttur{data.total === 1 ? "a" : "e"} su Hostland: case vacanze nel centro storico, appartamenti, B&amp;B di charme, affittacamere e boutique hotel. Ogni host ha pubblicato il proprio <strong>GuestBook digitale</strong> con consigli locali esclusivi: ristoranti consigliati, esperienze, sconti riservati e mappa interattiva dei punti di interesse.
            </p>
            {capacity_stats && (capacity_stats.min_guests || capacity_stats.max_guests) && (
              <p className="mt-3 text-xs text-zinc-500">
                Capacità ricettiva: <strong>{capacity_stats.min_guests}–{capacity_stats.max_guests} ospiti</strong> per struttura.
              </p>
            )}
          </div>
        </motion.div>

        {/* ─── Properties grid ─── */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mt-12">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-zinc-900 mb-6">
            {data.total} struttur{data.total === 1 ? "a a " : "e a "}{city}
            {region && <span className="text-zinc-400 font-bold text-lg md:text-2xl"> · {region}</span>}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {data.properties.map((p, i) => {
              const url = p.custom_slug ? `/s/${p.custom_slug}` : `/property/${p.property_id}`;
              const cover = p.media?.[0];
              return (
                <motion.article
                  key={p.property_id}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.04 }}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-3xl overflow-hidden border border-zinc-100 cursor-pointer shadow-sm hover:shadow-xl transition-shadow"
                  onClick={() => navigate(url)}
                  data-testid={`city-prop-${p.property_id}`}
                >
                  {cover ? (
                    <img
                      src={`${API}/files/${cover}?w=600`}
                      alt={`${p.name} a ${city}${province ? ` (${province_code || province})` : ""} — prenotazione diretta su Hostland`}
                      loading="lazy"
                      decoding="async"
                      width="600"
                      height="400"
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-zinc-100 to-zinc-200 flex items-center justify-center">
                      <MapPin className="w-8 h-8 text-zinc-300" />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-bold text-zinc-900 text-base truncate">{p.name}</h3>
                    <p className="text-xs text-zinc-400 truncate mt-0.5">{p.address || city}</p>
                    <div className="flex items-center gap-3 mt-3 text-[11px] text-zinc-500 font-medium">
                      {p.guests_count > 0 && <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {p.guests_count}</span>}
                      {p.rooms_count > 0 && <span className="inline-flex items-center gap-1"><Bed className="w-3 h-3" /> {p.rooms_count}</span>}
                      {p.bathrooms_count > 0 && <span className="inline-flex items-center gap-1"><Bath className="w-3 h-3" /> {p.bathrooms_count}</span>}
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-100">
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Zero commissioni</span>
                      <ArrowRight className="w-4 h-4 text-zinc-400" />
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </motion.div>

        {/* ─── FAQ ─── */}
        {faqs.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16"
            aria-labelledby="faq-heading"
            data-testid="city-faq-section"
          >
            <h2 id="faq-heading" className="text-2xl md:text-3xl font-black tracking-tight text-zinc-900 mb-6 flex items-center gap-2">
              <HelpCircle className="w-7 h-7 text-zinc-400" />
              Domande frequenti su {city}
            </h2>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <details
                  key={i}
                  className="group bg-white rounded-2xl border border-zinc-100 px-5 py-4 cursor-pointer hover:border-zinc-200 transition-colors"
                  data-testid={`city-faq-${i}`}
                >
                  <summary className="flex items-center justify-between gap-3 text-sm md:text-base font-bold text-zinc-900 list-none">
                    <span>{f.q}</span>
                    <ChevronRight className="w-4 h-4 text-zinc-400 transition-transform group-open:rotate-90 shrink-0" />
                  </summary>
                  <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </motion.section>
        )}

        {/* ─── Nearby cities (internal linking, boosts pagerank) ─── */}
        {nearby_cities && nearby_cities.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16"
            aria-labelledby="nearby-heading"
            data-testid="city-nearby-section"
          >
            <h2 id="nearby-heading" className="text-2xl md:text-3xl font-black tracking-tight text-zinc-900 mb-2">
              Altre città vicine a {city}
            </h2>
            <p className="text-sm text-zinc-500 mb-6">
              Esplora case vacanze senza commissioni in {province ? `provincia di ${province}` : "altre città italiane"} su Hostland.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {nearby_cities.map((n) => (
                <Link
                  key={n.slug}
                  to={`/case-vacanze/${n.slug}`}
                  className="group bg-white rounded-2xl border border-zinc-100 p-3 hover:border-zinc-900 hover:shadow-lg transition-all text-center"
                  data-testid={`city-nearby-${n.slug}`}
                >
                  <p className="font-bold text-sm text-zinc-900 truncate">{titleCase(n.comune)}</p>
                  <p className="text-[10px] text-zinc-400 mt-0.5 truncate">{n.province || n.region || "Italia"}</p>
                  <p className="text-[11px] font-bold mt-1.5" style={{ color: ACCENT }}>
                    {n.count} struttur{n.count === 1 ? "a" : "e"} →
                  </p>
                </Link>
              ))}
            </div>
          </motion.section>
        )}

        {/* ─── Footer SEO ─── */}
        <div className="mt-16 pt-8 border-t border-zinc-200 text-center">
          <p className="text-xs text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            <strong>Hostland</strong> è la piattaforma italiana per host e property manager che permette di pubblicare la propria struttura{region && ` a ${city}, ${region},`} senza pagare commissioni di intermediazione. <Link to="/" className="text-zinc-900 font-bold underline" data-testid="city-discover-hostland">Scopri Hostland</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CityLanding;
